import React, { useState, useEffect, useMemo } from 'react';
import {
  Settings,
  Search,
  Building,
  DollarSign,
  Percent,
  Save,
  RefreshCw,
  Info,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronRight,
  MapPin,
  FileText,
  AlertTriangle,
  Filter,
  BarChart3,
  Edit,
  Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from 'sonner';
import { db } from '../../../lib/db';
import { 
  STATE_TAX_CONFIG, 
  calculateStateTaxFromConfig, 
  getStatesWithIncomeTax,
  getStateDropdownOptions 
} from '../../../lib/stateTaxData';
import { formatUSDCurrency } from '../../../lib/usPayrollCalculations';

// Filter categories
const STATE_CATEGORIES = [
  { value: 'all', label: 'All States' },
  { value: 'with-tax', label: 'States with Income Tax' },
  { value: 'no-tax', label: 'No Income Tax States' },
  { value: 'flat', label: 'Flat Tax States' },
  { value: 'progressive', label: 'Progressive Tax States' },
  { value: 'local-tax', label: 'States with Local Taxes' },
  { value: 'sdi', label: 'SDI States' }
];

export default function StateTaxSettings() {
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedState, setSelectedState] = useState(null);
  const [showStateDetail, setShowStateDetail] = useState(false);
  const [companyStateTaxSettings, setCompanyStateTaxSettings] = useState({});
  const [showCalculator, setShowCalculator] = useState(false);
  const [calculatorState, setCalculatorState] = useState('CA');
  const [editingStateOverride, setEditingStateOverride] = useState(null);
  const [calculatorParams, setCalculatorParams] = useState({
    annualIncome: 75000,
    filingStatus: 'single'
  });

  useEffect(() => {
    loadCompanySettings();
  }, []);

  const loadCompanySettings = async () => {
    setLoading(true);
    try {
      // Load company-specific tax settings from database
      const settings = await db.settings.get('stateTaxSettings');
      if (settings) {
        setCompanyStateTaxSettings(settings.value || settings.data || {});
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveCompanySettings = async () => {
    try {
      await db.settings.put({
        key: 'stateTaxSettings',
        value: companyStateTaxSettings,
        data: companyStateTaxSettings, // Keep for backward compatibility
        updatedAt: Date.now()
      });
      toast.success('State tax settings saved');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    }
  };

  // Get all states with their configuration
  const allStates = useMemo(() => {
    return Object.entries(STATE_TAX_CONFIG).map(([code, config]) => ({
      code,
      ...config
    }));
  }, []);

  // Filter states based on search and category
  const filteredStates = useMemo(() => {
    return allStates.filter(state => {
      // Search filter
      const matchesSearch = !searchQuery || 
        state.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        state.code.toLowerCase().includes(searchQuery.toLowerCase());

      // Category filter
      let matchesCategory = true;
      switch (categoryFilter) {
        case 'with-tax':
          matchesCategory = state.hasStateTax;
          break;
        case 'no-tax':
          matchesCategory = !state.hasStateTax;
          break;
        case 'flat':
          matchesCategory = state.hasStateTax && state.taxType === 'flat';
          break;
        case 'progressive':
          matchesCategory = state.hasStateTax && state.taxType === 'progressive';
          break;
        case 'local-tax':
          matchesCategory = state.hasLocalTax;
          break;
        case 'sdi':
          matchesCategory = state.otherTaxes?.sdiRequired;
          break;
        default:
          matchesCategory = true;
      }

      return matchesSearch && matchesCategory;
    });
  }, [allStates, searchQuery, categoryFilter]);

  // Statistics
  const statistics = useMemo(() => {
    const withTax = allStates.filter(s => s.hasStateTax).length;
    const noTax = allStates.filter(s => !s.hasStateTax).length;
    const flatTax = allStates.filter(s => s.taxType === 'flat').length;
    const progressive = allStates.filter(s => s.taxType === 'progressive').length;
    const localTax = allStates.filter(s => s.hasLocalTax).length;
    const sdi = allStates.filter(s => s.otherTaxes?.sdiRequired).length;

    return { withTax, noTax, flatTax, progressive, localTax, sdi };
  }, [allStates]);

  const getTopTaxRate = (state) => {
    if (!state.hasStateTax) return 'N/A';
    if (state.taxType === 'flat' && state.flatRate) {
      return `${(state.flatRate * 100).toFixed(2)}%`;
    }
    if (state.brackets?.single) {
      const topBracket = state.brackets.single[state.brackets.single.length - 1];
      return `${(topBracket.rate * 100).toFixed(2)}%`;
    }
    return 'N/A';
  };

  const getTaxTypeLabel = (state) => {
    if (!state.hasStateTax) return 'None';
    if (state.taxType === 'flat') return 'Flat';
    if (state.taxType === 'progressive') return 'Progressive';
    return 'Unknown';
  };

  const viewStateDetails = (state) => {
    setSelectedState(state);
    setShowStateDetail(true);
  };

  const editStateSettings = (state) => {
    setSelectedState(state);
    setShowStateDetail(true);
    // Slight delay to ensure dialog opens first, then switch to company tab
    setTimeout(() => {
      const companyTab = document.querySelector('[data-state="inactive"][value="company"]');
      if (companyTab) companyTab.click();
    }, 100);
  };

  const calculateTax = () => {
    const state = STATE_TAX_CONFIG[calculatorState];
    if (!state) return null;

    const result = calculateStateTaxFromConfig(
      calculatorParams.annualIncome,
      calculatorState,
      calculatorParams.filingStatus
    );

    return result;
  };

  const calculatedTax = showCalculator ? calculateTax() : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">State Tax Settings</h1>
          <p className="text-gray-500 mt-1">
            Configure state income tax settings for payroll processing
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowCalculator(true)}>
            <BarChart3 className="h-4 w-4 mr-2" />
            Tax Calculator
          </Button>
          <Button onClick={saveCompanySettings}>
            <Save className="h-4 w-4 mr-2" />
            Save Settings
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className="cursor-pointer hover:border-blue-300" onClick={() => setCategoryFilter('with-tax')}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">With Tax</p>
                <p className="text-2xl font-bold">{statistics.withTax}</p>
              </div>
              <DollarSign className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-blue-300" onClick={() => setCategoryFilter('no-tax')}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">No Tax</p>
                <p className="text-2xl font-bold">{statistics.noTax}</p>
              </div>
              <XCircle className="h-8 w-8 text-gray-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-blue-300" onClick={() => setCategoryFilter('flat')}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Flat Tax</p>
                <p className="text-2xl font-bold">{statistics.flatTax}</p>
              </div>
              <Percent className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-blue-300" onClick={() => setCategoryFilter('progressive')}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Progressive</p>
                <p className="text-2xl font-bold">{statistics.progressive}</p>
              </div>
              <BarChart3 className="h-8 w-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-blue-300" onClick={() => setCategoryFilter('local-tax')}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Local Taxes</p>
                <p className="text-2xl font-bold">{statistics.localTax}</p>
              </div>
              <Building className="h-8 w-8 text-orange-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-blue-300" onClick={() => setCategoryFilter('sdi')}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">SDI States</p>
                <p className="text-2xl font-bold">{statistics.sdi}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-amber-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search states..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-52">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATE_CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="text-sm text-gray-500">
              Showing {filteredStates.length} of {allStates.length} states
            </div>
          </div>
        </CardContent>
      </Card>

      {/* States Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>State</TableHead>
                <TableHead>Tax Type</TableHead>
                <TableHead>Top Rate</TableHead>
                <TableHead>Local Tax</TableHead>
                <TableHead>SDI/PFML</TableHead>
                <TableHead>Standard Deduction</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStates.map(state => (
                <TableRow key={state.code} className="hover:bg-gray-50">
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-xs">
                        {state.code}
                      </div>
                      <div>
                        <div className="font-medium">{state.name}</div>
                        {state.notes && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <span className="text-xs text-gray-400 flex items-center gap-1">
                                  <Info className="h-3 w-3" /> Note
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs max-w-xs">{state.notes}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant="outline" 
                      className={
                        !state.hasStateTax ? 'bg-gray-50 text-gray-500' :
                        state.taxType === 'flat' ? 'bg-blue-50 text-blue-700' :
                        'bg-purple-50 text-purple-700'
                      }
                    >
                      {getTaxTypeLabel(state)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className={!state.hasStateTax ? 'text-gray-400' : 'font-medium'}>
                      {getTopTaxRate(state)}
                    </span>
                  </TableCell>
                  <TableCell>
                    {state.hasLocalTax ? (
                      <Badge variant="outline" className="bg-orange-50 text-orange-700">
                        <Building className="h-3 w-3 mr-1" />
                        Yes
                      </Badge>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      {state.otherTaxes?.sdiRequired && (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 text-xs">
                          SDI {((state.otherTaxes.sdiRate || 0) * 100).toFixed(2)}%
                        </Badge>
                      )}
                      {state.otherTaxes?.pfmlRequired && (
                        <Badge variant="outline" className="bg-teal-50 text-teal-700 text-xs">
                          PFML {((state.otherTaxes.pfmlRate || 0) * 100).toFixed(2)}%
                        </Badge>
                      )}
                      {!state.otherTaxes?.sdiRequired && !state.otherTaxes?.pfmlRequired && (
                        <span className="text-gray-400">-</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {state.standardDeduction?.single ? (
                      <span className="text-sm">
                        ${state.standardDeduction.single.toLocaleString()}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => viewStateDetails(state)}>
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      {state.hasStateTax && (
                        <Button variant="ghost" size="sm" onClick={() => editStateSettings(state)} className="text-blue-600 hover:text-blue-700">
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* State Detail Modal */}
      <Dialog open={showStateDetail} onOpenChange={setShowStateDetail}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-10 h-10 rounded bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                {selectedState?.code}
              </div>
              <span>{selectedState?.name} Tax Details</span>
            </DialogTitle>
            <DialogDescription>
              Complete tax configuration for {selectedState?.name}
            </DialogDescription>
          </DialogHeader>

          {selectedState && (
            <Tabs defaultValue="overview" className="mt-4">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="brackets">Tax Brackets</TabsTrigger>
                <TabsTrigger value="other">Other Taxes</TabsTrigger>
                <TabsTrigger value="company">Company Overrides</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-gray-500">Tax Type</p>
                      <p className="text-lg font-bold">
                        {selectedState.hasStateTax ? 
                          (selectedState.taxType === 'flat' ? 'Flat Rate' : 'Progressive') : 
                          'No State Income Tax'}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-gray-500">Top Tax Rate</p>
                      <p className="text-lg font-bold">{getTopTaxRate(selectedState)}</p>
                    </CardContent>
                  </Card>
                </div>

                {selectedState.flatRate && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Flat Tax Rate</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-blue-600">
                        {(selectedState.flatRate * 100).toFixed(2)}%
                      </p>
                    </CardContent>
                  </Card>
                )}

                {selectedState.standardDeduction && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Standard Deductions</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">Single</p>
                          <p className="font-bold">${selectedState.standardDeduction.single?.toLocaleString() || 0}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Married</p>
                          <p className="font-bold">${selectedState.standardDeduction.married?.toLocaleString() || 0}</p>
                        </div>
                        {selectedState.standardDeduction.headOfHousehold && (
                          <div>
                            <p className="text-sm text-gray-500">Head of Household</p>
                            <p className="font-bold">${selectedState.standardDeduction.headOfHousehold.toLocaleString()}</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {selectedState.notes && (
                  <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="pt-4">
                      <div className="flex gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                        <p className="text-sm text-amber-800">{selectedState.notes}</p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="brackets" className="space-y-4">
                {selectedState.brackets ? (
                  <>
                    {['single', 'married', 'headOfHousehold'].map(filingStatus => 
                      selectedState.brackets[filingStatus] && (
                        <Card key={filingStatus}>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base capitalize">
                              {filingStatus === 'headOfHousehold' ? 'Head of Household' : filingStatus} Filer Brackets
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Income Range</TableHead>
                                  <TableHead>Rate</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {selectedState.brackets[filingStatus].map((bracket, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>
                                      {formatUSDCurrency(bracket.min)} - {bracket.max === Infinity ? '∞' : formatUSDCurrency(bracket.max)}
                                    </TableCell>
                                    <TableCell className="font-medium">
                                      {(bracket.rate * 100).toFixed(2)}%
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </CardContent>
                        </Card>
                      )
                    )}
                  </>
                ) : (
                  <Card>
                    <CardContent className="pt-4 text-center text-gray-500">
                      {selectedState.hasStateTax ? 
                        'This state uses a flat tax rate' : 
                        'This state has no income tax'}
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="other" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Local Taxes</p>
                          <p className="font-bold">{selectedState.hasLocalTax ? 'Required' : 'Not Required'}</p>
                        </div>
                        {selectedState.hasLocalTax ? 
                          <CheckCircle className="h-6 w-6 text-green-500" /> :
                          <XCircle className="h-6 w-6 text-gray-300" />
                        }
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Reciprocity</p>
                          <p className="font-bold">{selectedState.reciprocity ? 'Yes' : 'No'}</p>
                        </div>
                        {selectedState.reciprocity ? 
                          <CheckCircle className="h-6 w-6 text-green-500" /> :
                          <XCircle className="h-6 w-6 text-gray-300" />
                        }
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {selectedState.otherTaxes && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Additional State Taxes</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {selectedState.otherTaxes.sdiRequired && (
                          <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                            <div>
                              <p className="font-medium">State Disability Insurance (SDI)</p>
                              <p className="text-sm text-gray-500">
                                Wage Base: ${selectedState.otherTaxes.sdiWageBase?.toLocaleString() || 'N/A'}
                              </p>
                            </div>
                            <Badge className="bg-amber-100 text-amber-800">
                              {((selectedState.otherTaxes.sdiRate || 0) * 100).toFixed(3)}%
                            </Badge>
                          </div>
                        )}

                        {selectedState.otherTaxes.pfmlRequired && (
                          <div className="flex justify-between items-center p-3 bg-teal-50 rounded-lg">
                            <div>
                              <p className="font-medium">Paid Family & Medical Leave (PFML)</p>
                              <p className="text-sm text-gray-500">
                                Employee Share: {((selectedState.otherTaxes.pfmlEmployeeShare || 0) * 100).toFixed(2)}%
                              </p>
                            </div>
                            <Badge className="bg-teal-100 text-teal-800">
                              {((selectedState.otherTaxes.pfmlRate || 0) * 100).toFixed(3)}%
                            </Badge>
                          </div>
                        )}

                        {selectedState.otherTaxes.suiWageBase && (
                          <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                            <div>
                              <p className="font-medium">State Unemployment (SUI)</p>
                              <p className="text-sm text-gray-500">Employer Tax</p>
                            </div>
                            <Badge className="bg-blue-100 text-blue-800">
                              Wage Base: ${selectedState.otherTaxes.suiWageBase.toLocaleString()}
                            </Badge>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {selectedState.reciprocity && selectedState.reciprocityStates?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Reciprocity Agreements</CardTitle>
                      <CardDescription>
                        Employees from these states may be exempt from {selectedState.name} withholding
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {selectedState.reciprocityStates.map(st => (
                          <Badge key={st} variant="outline">{st}</Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Company Overrides Tab */}
              <TabsContent value="company" className="space-y-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Edit className="h-4 w-4" />
                      Company-Specific Overrides for {selectedState.name}
                    </CardTitle>
                    <CardDescription>
                      Override default tax settings for your company. These settings take priority over system defaults.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {selectedState.hasStateTax ? (
                      <>
                        {/* Additional Withholding */}
                        <div className="space-y-2">
                          <Label>Default Additional Withholding (per pay period)</Label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              value={companyStateTaxSettings[selectedState.code]?.additionalWithholding || ''}
                              onChange={(e) => setCompanyStateTaxSettings(prev => ({
                                ...prev,
                                [selectedState.code]: {
                                  ...prev[selectedState.code],
                                  additionalWithholding: parseFloat(e.target.value) || 0
                                }
                              }))}
                              className="pl-9"
                            />
                          </div>
                          <p className="text-xs text-gray-500">Extra amount to withhold from all {selectedState.name} employees</p>
                        </div>

                        {/* Tax Rate Adjustment */}
                        {selectedState.taxType === 'flat' && (
                          <div className="space-y-2">
                            <Label>Tax Rate Override (%)</Label>
                            <div className="relative">
                              <Percent className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <Input
                                type="number"
                                min="0"
                                max="20"
                                step="0.01"
                                placeholder={`Default: ${(selectedState.flatRate * 100).toFixed(2)}%`}
                                value={companyStateTaxSettings[selectedState.code]?.rateOverride !== undefined 
                                  ? (companyStateTaxSettings[selectedState.code].rateOverride * 100).toFixed(2) 
                                  : ''}
                                onChange={(e) => setCompanyStateTaxSettings(prev => ({
                                  ...prev,
                                  [selectedState.code]: {
                                    ...prev[selectedState.code],
                                    rateOverride: parseFloat(e.target.value) / 100 || null
                                  }
                                }))}
                                className="pl-9"
                              />
                            </div>
                            <p className="text-xs text-gray-500">Leave blank to use default rate of {(selectedState.flatRate * 100).toFixed(2)}%</p>
                          </div>
                        )}

                        {/* Standard Deduction Override */}
                        <div className="space-y-2">
                          <Label>Standard Deduction Override (Single)</Label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                              type="number"
                              min="0"
                              step="100"
                              placeholder={`Default: $${(selectedState.standardDeduction?.single || 0).toLocaleString()}`}
                              value={companyStateTaxSettings[selectedState.code]?.standardDeductionSingle || ''}
                              onChange={(e) => setCompanyStateTaxSettings(prev => ({
                                ...prev,
                                [selectedState.code]: {
                                  ...prev[selectedState.code],
                                  standardDeductionSingle: parseFloat(e.target.value) || null
                                }
                              }))}
                              className="pl-9"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>Standard Deduction Override (Married)</Label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                              type="number"
                              min="0"
                              step="100"
                              placeholder={`Default: $${(selectedState.standardDeduction?.married || 0).toLocaleString()}`}
                              value={companyStateTaxSettings[selectedState.code]?.standardDeductionMarried || ''}
                              onChange={(e) => setCompanyStateTaxSettings(prev => ({
                                ...prev,
                                [selectedState.code]: {
                                  ...prev[selectedState.code],
                                  standardDeductionMarried: parseFloat(e.target.value) || null
                                }
                              }))}
                              className="pl-9"
                            />
                          </div>
                        </div>

                        {/* Exemption Settings */}
                        <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                          <div>
                            <Label className="text-amber-800">Disable {selectedState.name} Withholding</Label>
                            <p className="text-xs text-amber-700">Only use if your company is exempt from this state's taxes</p>
                          </div>
                          <Switch
                            checked={companyStateTaxSettings[selectedState.code]?.disabled || false}
                            onCheckedChange={(checked) => setCompanyStateTaxSettings(prev => ({
                              ...prev,
                              [selectedState.code]: {
                                ...prev[selectedState.code],
                                disabled: checked
                              }
                            }))}
                          />
                        </div>

                        {/* SDI/PFML Overrides if applicable */}
                        {selectedState.otherTaxes?.sdiRequired && (
                          <div className="space-y-2">
                            <Label>SDI Rate Override (%)</Label>
                            <div className="relative">
                              <Percent className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <Input
                                type="number"
                                min="0"
                                max="5"
                                step="0.001"
                                placeholder={`Default: ${((selectedState.otherTaxes.sdiRate || 0) * 100).toFixed(3)}%`}
                                value={companyStateTaxSettings[selectedState.code]?.sdiRateOverride !== undefined 
                                  ? (companyStateTaxSettings[selectedState.code].sdiRateOverride * 100).toFixed(3) 
                                  : ''}
                                onChange={(e) => setCompanyStateTaxSettings(prev => ({
                                  ...prev,
                                  [selectedState.code]: {
                                    ...prev[selectedState.code],
                                    sdiRateOverride: parseFloat(e.target.value) / 100 || null
                                  }
                                }))}
                                className="pl-9"
                              />
                            </div>
                          </div>
                        )}

                        <div className="flex justify-end gap-2 pt-4 border-t">
                          <Button 
                            variant="outline" 
                            onClick={() => {
                              setCompanyStateTaxSettings(prev => {
                                const updated = { ...prev };
                                delete updated[selectedState.code];
                                return updated;
                              });
                              toast.success('Overrides cleared for ' + selectedState.name);
                            }}
                          >
                            Clear Overrides
                          </Button>
                          <Button onClick={async () => {
                            await saveCompanySettings();
                            toast.success('Saved overrides for ' + selectedState.name);
                          }}>
                            <Save className="h-4 w-4 mr-2" />
                            Save Overrides
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                        <p className="font-medium">{selectedState.name} has no state income tax</p>
                        <p className="text-sm">No configuration overrides are needed for this state.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Show current overrides summary */}
                {companyStateTaxSettings[selectedState.code] && Object.keys(companyStateTaxSettings[selectedState.code]).length > 0 && (
                  <Card className="bg-blue-50 border-blue-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base text-blue-800">Active Overrides</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        {companyStateTaxSettings[selectedState.code].additionalWithholding > 0 && (
                          <div className="flex justify-between">
                            <span className="text-blue-700">Additional Withholding:</span>
                            <span className="font-medium">${companyStateTaxSettings[selectedState.code].additionalWithholding.toFixed(2)}</span>
                          </div>
                        )}
                        {companyStateTaxSettings[selectedState.code].rateOverride && (
                          <div className="flex justify-between">
                            <span className="text-blue-700">Rate Override:</span>
                            <span className="font-medium">{(companyStateTaxSettings[selectedState.code].rateOverride * 100).toFixed(2)}%</span>
                          </div>
                        )}
                        {companyStateTaxSettings[selectedState.code].disabled && (
                          <div className="flex justify-between">
                            <span className="text-blue-700">State Withholding:</span>
                            <Badge variant="destructive">Disabled</Badge>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStateDetail(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tax Calculator Modal */}
      <Dialog open={showCalculator} onOpenChange={setShowCalculator}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>State Tax Calculator</DialogTitle>
            <DialogDescription>
              Calculate estimated state income tax for any state
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>State</Label>
                <Select 
                  value={calculatorState} 
                  onValueChange={setCalculatorState}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {getStateDropdownOptions().map(st => (
                      <SelectItem key={st.code} value={st.code}>
                        {st.name} ({st.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Filing Status</Label>
                <Select 
                  value={calculatorParams.filingStatus} 
                  onValueChange={(v) => setCalculatorParams(prev => ({ ...prev, filingStatus: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Single</SelectItem>
                    <SelectItem value="married">Married Filing Jointly</SelectItem>
                    <SelectItem value="marriedSeparate">Married Filing Separately</SelectItem>
                    <SelectItem value="headOfHousehold">Head of Household</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Annual Taxable Income</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="number"
                  value={calculatorParams.annualIncome}
                  onChange={(e) => setCalculatorParams(prev => ({ 
                    ...prev, 
                    annualIncome: parseFloat(e.target.value) || 0 
                  }))}
                  className="pl-9"
                />
              </div>
            </div>

            {calculatedTax && (
              <Card className="bg-gray-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Calculation Results</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">State</span>
                    <span className="font-medium">{calculatedTax.stateName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Annual Tax</span>
                    <span className="font-bold text-lg">{formatUSDCurrency(calculatedTax.tax)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monthly Tax</span>
                    <span className="font-medium">{formatUSDCurrency(calculatedTax.tax / 12)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Effective Rate</span>
                    <span className="font-medium">{calculatedTax.effectiveRate}%</span>
                  </div>
                  {calculatedTax.marginalRate && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Marginal Rate</span>
                      <span className="font-medium">{calculatedTax.marginalRate.toFixed(2)}%</span>
                    </div>
                  )}
                  {!calculatedTax.hasStateTax && (
                    <div className="bg-green-50 p-3 rounded-lg text-center">
                      <CheckCircle className="h-6 w-6 text-green-500 mx-auto mb-2" />
                      <p className="text-green-700 font-medium">No State Income Tax</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCalculator(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
